import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useComments, type UseCommentsTarget } from '../../hooks/useComments'
import type { CommentDTO } from '../../api/comments'
import { getToken } from '../../lib/auth'

type Props = {
  target: UseCommentsTarget
}

export default function ReviewsInline({ target }: Props) {
  const { items, canLoadMore, loadMore, create, react } = useComments(target, 'top')
  const label = target.type === 'post' ? 'Comments' : 'Reviews'
  const noun = target.type === 'post' ? 'comment' : 'review'

  // Group parent/replies like in drawer
  const { parents, repliesByParent } = useMemo(() => {
    const parents: CommentDTO[] = []
    const replies = new Map<string | number, CommentDTO[]>()
    items.forEach((c) => {
      if (c.parent_id) {
        const list = replies.get(c.parent_id) ?? []
        list.push(c)
        replies.set(c.parent_id, list)
      } else {
        parents.push(c)
      }
    })
    replies.forEach((list, key) => {
      list.sort((a, b) => {
        const at = a.created_at ? new Date(a.created_at).getTime() : 0
        const bt = b.created_at ? new Date(b.created_at).getTime() : 0
        return bt - at
      })
      replies.set(key, list)
    })
    return { parents, repliesByParent: replies }
  }, [items])

  const [expanded, setExpanded] = useState<Set<string | number>>(new Set())
  const toggleExpanded = useCallback((id: string | number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const authed = Boolean(getToken())
  const composerRef = useRef<HTMLTextAreaElement | null>(null)
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [replyTo, setReplyTo] = useState<CommentDTO | null>(null)

  async function handleSubmit() {
    const body = text.trim()
    if (!body) return
    setSubmitting(true)
    try {
      const parentId = replyTo?.id ?? null
      await create(body, parentId || null)
      if (parentId) setExpanded((s) => new Set(s).add(parentId))
      setText('')
      setReplyTo(null)
    } finally {
      setSubmitting(false)
    }
  }

  // Enhance code blocks in comment bodies
  useEffect(() => {
    try {
      const root = document.getElementById('reviews-inline-root')
      if (root) (window as any).Prism?.highlightAllUnder?.(root)
    } catch {}
  }, [parents, repliesByParent])

  const renderBody = (c: CommentDTO) => {
    const html = c.body_html || ((window as any).marked?.parse?.(c.body || '') ?? '')
    const safe = ((window as any).DOMPurify?.sanitize?.(html)) ?? html
    return <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: safe }} />
  }

  return (
    <section id="reviews" className="mt-10" aria-label={label}>
      <div className="mb-4 flex items-center gap-2">
        <div className="text-base font-extrabold">{label}</div>
        <div className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs">{items.length}</div>
      </div>

      <div id="reviews-inline-root">
        {parents.map((p) => {
          const replies = repliesByParent.get(p.id) ?? []
          const isOpen = expanded.has(p.id)
          const likeCount = (p.reactions?.like ?? 0) as number
          const liked = p.my_reaction === 'like'
          const time = p.created_at ? new Date(p.created_at).toLocaleDateString() : ''
          return (
            <article key={String(p.id)} className="py-6 border-t border-white/10 first:border-t-0">
              <header className="mb-2 flex items-center gap-3 text-sm">
                {p.author?.avatar_url ? (
                  <img src={p.author.avatar_url} className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-white/10" />
                )}
                <div className="font-semibold">{p.author?.name || 'Anonymous'}</div>
                {p.author?.handle && <div className="text-white/70">{p.author.handle}</div>}
                <div className="ml-auto text-white/60 text-xs">{time}</div>
              </header>
              {renderBody(p)}
              <div className="mt-2 flex items-center gap-4 text-sm">
                <button onClick={() => react(p.id, 'like')} className="flex items-center gap-1 hover:opacity-90">
                  <i className={`fa-solid fa-heart ${liked ? 'text-red-500' : 'text-white/85'}`} />
                  <span className="text-white/80">{likeCount}</span>
                </button>
                <button onClick={() => setReplyTo(p)} className="flex items-center gap-1 text-white/85 hover:opacity-90">
                  <i className="fa-solid fa-reply" />
                  <span>Reply</span>
                </button>
              </div>

              {replies.length > 0 && !isOpen && (
                <button onClick={() => toggleExpanded(p.id)} className="mt-3 text-sm text-white/80 underline">
                  View {replies.length} repl{replies.length === 1 ? 'y' : 'ies'}
                </button>
              )}

              {isOpen && (
                <div className="mt-4 pl-6 border-l border-white/10 space-y-6">
                  {replies.map((r) => {
                    const rLike = (r.reactions?.like ?? 0) as number
                    const rLiked = r.my_reaction === 'like'
                    const rTime = r.created_at ? new Date(r.created_at).toLocaleDateString() : ''
                    return (
                      <div key={String(r.id)}>
                        <div className="mb-2 flex items-center gap-3 text-sm">
                          {r.author?.avatar_url ? (
                            <img src={r.author.avatar_url} className="h-7 w-7 rounded-full object-cover" />
                          ) : (
                            <div className="h-7 w-7 rounded-full bg-white/10" />
                          )}
                          <div className="font-semibold">{r.author?.name || 'Anonymous'}</div>
                          {r.author?.handle && <div className="text-white/70">{r.author.handle}</div>}
                          <div className="ml-auto text-white/60 text-xs">{rTime}</div>
                        </div>
                        {renderBody(r)}
                        <div className="mt-2 flex items-center gap-4 text-sm">
                          <button onClick={() => react(r.id, 'like')} className="flex items-center gap-1 hover:opacity-90">
                            <i className={`fa-solid fa-heart ${rLiked ? 'text-red-500' : 'text-white/85'}`} />
                            <span className="text-white/80">{rLike}</span>
                          </button>
                          <button onClick={() => setReplyTo(r)} className="flex items-center gap-1 text-white/85 hover:opacity-90">
                            <i className="fa-solid fa-reply" />
                            <span>Reply</span>
                          </button>
                        </div>
                      </div>
                    )
                  })}
                  <button onClick={() => toggleExpanded(p.id)} className="text-sm text-white/70 underline">Show less</button>
                </div>
              )}
            </article>
          )
        })}

        {canLoadMore && (
          <div className="py-6">
            <button onClick={loadMore} className="text-sm text-white/85 underline">Load more {label.toLowerCase()}...</button>
          </div>
        )}
      </div>

      {/* Inline composer */}
      <div className="mt-8 border-t border-white/10 pt-4">
        {!authed ? (
          <a href="/login" className="text-sm text-white/85 underline">Log in to write a {noun}</a>
        ) : (
          <div>
            {replyTo && (
              <div className="mb-2 text-xs text-white/80">
                Replying to {replyTo.author?.name || replyTo.author?.handle || 'comment'}
                <button onClick={() => setReplyTo(null)} className="ml-2 underline">Cancel</button>
              </div>
            )}
            <textarea
              ref={composerRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Write a ${noun}... (Markdown supported)`}
              rows={4}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-brand chat-scroll"
            />
            <div className="mt-2">
              <button onClick={handleSubmit} disabled={submitting || !text.trim()} className="px-3 py-2 rounded-lg bg-brand/80 hover:bg-brand disabled:opacity-60">Post {noun}</button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
